import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ForcePasswordChange() {
  const { user, logout, refreshUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refreshUser();
      toast.success("Password updated — welcome!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9FAFB]" data-testid="force-password-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-gray-200 rounded-sm shadow-sm p-7" data-testid="force-password-form">
        <div className="w-11 h-11 rounded-sm bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
          <ShieldAlert className="w-5 h-5" />
        </div>
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-gray-900">Update your password</h2>
        <p className="text-gray-500 text-sm mt-1.5 mb-6">
          For security, an administrator requires you to set a new password before continuing.
        </p>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Current password</label>
            <input data-testid="force-current-password-input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>New password</label>
            <input data-testid="force-new-password-input" type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Confirm new password</label>
            <input data-testid="force-confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={inputCls} />
          </div>
        </div>

        <button type="submit" disabled={saving} data-testid="force-change-password-button"
          className="w-full mt-6 bg-[#002FA7] hover:bg-[#002480] text-white py-2.5 rounded-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save and continue
        </button>

        <div className="mt-5 flex items-center justify-between text-xs text-gray-400">
          <span>Signed in as {user?.email}</span>
          <button type="button" onClick={logout} data-testid="force-logout-button" className="font-semibold text-[#002FA7] hover:underline">Sign out</button>
        </div>
      </form>
    </div>
  );
}
